import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { type OccurrenceResponse } from '@kidshome/api-contract';
import { formatPoints } from '@kidshome/i18n';
import { Card } from '@kidshome/ui';
import { LoadingState, ErrorState } from '@/shared/ui';
import { useInbox } from '@/widgets/inbox';
import { useFeature } from '@/entities/session';
import { Feature } from '@/shared/lib/features';
import { useHouseholdContext } from '@/shared/lib/household';
import { useHouseholdDayTasks } from '@/shared/lib/household-tasks';
import { localDateISO } from '@/shared/lib/dates';
import { useMembersGuarded } from '@/entities/member/use-members-guarded';

/**
 * "Today at a glance" (mockup 02): the guardian's 3-up daily signal — Done · To approve · Planned.
 * "Done" and "Planned" are today's chore progress, computed from a fan-out of `GET /v1/tasks/day`
 * across the household's children for the household-local today. The "To approve" tile links to and
 * MIRRORS the Inbox: it shares `useInbox().totalPending` with the nav badge so the number can never
 * disagree with the badge or the Inbox list — it counts every pending decision (chore approvals across
 * all dates + reward redemptions + pending rewards), not just today's chores. If the Inbox queue
 * itself errors it degrades to today's pending-approval chores rather than blanking the whole card.
 * Errors surface as an error state (with retry), never masked as zeros.
 */
function tallyDay(occurrences: readonly OccurrenceResponse[]): {
  done: number;
  toApprove: number;
  planned: number;
} {
  let done = 0;
  let toApprove = 0;
  let planned = 0;
  for (const o of occurrences) {
    if (o.status === 'approved' || o.status === 'skipped') done += 1;
    else if (o.status === 'pending_approval') toApprove += 1;
    else planned += 1; // planned / rejected / missed → still "to do" for the day
  }
  return { done, toApprove, planned };
}

export function TodaySummary(): React.ReactElement | null {
  const { t } = useTranslation('family');
  const { locale, timeZone } = useHouseholdContext();
  const members = useMembersGuarded();
  // Same source as the nav badge (already mounted by AppShell, so this just reads the shared cache).
  const inbox = useInbox();
  // The day-tasks fan-out is Chores-module data (ADR-0076) — a household that has turned Chores off
  // has no day tasks to read, so don't fire the (guaranteed-403) request at all; treat it the same as
  // an empty childIds list (trivially resolved, zero occurrences), never as pending/error.
  const choresActive = useFeature()(Feature.Chores);

  const childIds = useMemo(
    () => (members.data?.data ?? []).filter((m) => m.kind === 'child').map((m) => m.id),
    [members.data],
  );
  const today = localDateISO(timeZone);
  const day = useHouseholdDayTasks(childIds, today, choresActive && !members.isPending);

  // A guardian whose role lacks `member:read` has nothing honest to tally here (the tiles would
  // otherwise silently read 0/0/0 from an empty childIds list) — quietly hide the card (ADR-0137)
  // rather than show a false "all done" glance.
  if (members.forbidden) return null;
  if (members.isPending || (choresActive && day.isPending) || inbox.isPending) {
    return <LoadingState />;
  }
  if (members.isError) return <ErrorState onRetry={() => void members.refetch()} />;
  if (choresActive && day.isError) {
    return (
      <Card className="p-4">
        <ErrorState onRetry={() => day.refetch()} />
      </Card>
    );
  }

  const {
    done,
    toApprove: todayToApprove,
    planned,
  } = tallyDay(choresActive ? day.occurrences : []);
  // Mirror the Inbox badge exactly; degrade to today's chore approvals if the Inbox queue errored
  // (a feature/permission-disabled queue isn't an error — it resolves to 0).
  const toApprove = inbox.isError ? todayToApprove : inbox.totalPending;
  const tiles = [
    { key: 'done', labelKey: 'dashboard.glanceDone', value: done, tone: 'text-success' },
    {
      key: 'approve',
      labelKey: 'dashboard.glanceToApprove',
      value: toApprove,
      tone: 'text-warning',
    },
    { key: 'planned', labelKey: 'dashboard.glancePlanned', value: planned, tone: 'text-text' },
  ] as const;

  return (
    <Card className="p-4">
      <p className="mb-3 text-xs font-bold uppercase tracking-wide text-text-muted">
        {t('dashboard.glanceTitle')}
      </p>
      <div className="grid grid-cols-3 gap-2 text-center">
        {tiles.map((tile) =>
          tile.key === 'approve' ? (
            <Link
              key={tile.key}
              to="/inbox"
              className="flex flex-col items-center gap-1 rounded-md p-1 outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className={`font-display text-2xl font-bold ${tile.tone}`}>
                {formatPoints(locale, tile.value)}
              </span>
              <span className="text-xs font-semibold text-text-muted">{t(tile.labelKey)}</span>
            </Link>
          ) : (
            <div key={tile.key} className="flex flex-col items-center gap-1 p-1">
              <span className={`font-display text-2xl font-bold ${tile.tone}`}>
                {formatPoints(locale, tile.value)}
              </span>
              <span className="text-xs font-semibold text-text-muted">{t(tile.labelKey)}</span>
            </div>
          ),
        )}
      </div>
    </Card>
  );
}
